/**
 * Copy utilities for Ask AI feature
 * Cross-browser compatible including iOS Safari
 * Uses the same approach as CopyTextOrLinkToClipboard in utility.js
 */

/**
 * Check if device is iOS (iPad/iPhone)
 * @returns {boolean}
 */
const isIOSDevice = () => {
    const userAgentRegex = /ipad|iphone/i;
    return userAgentRegex.test(navigator.userAgent);
};

/**
 * Copy text to clipboard using fallback method for iOS Safari compatibility
 * This is the same approach used in CopyTextOrLinkToClipboard (utility.js)
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} - Success status
 */
const copyTextFallback = (text) => {
    return new Promise((resolve) => {
        try {
            const tempInput = document.createElement("textarea");
            tempInput.value = text;
            tempInput.setAttribute("readonly", ""); // Prevent keyboard from appearing on mobile
            tempInput.style.position = "absolute";
            tempInput.style.left = "-9999px";
            tempInput.style.top = "0";
            document.body.appendChild(tempInput);

            if (isIOSDevice()) {
                // iOS-specific selection handling
                const range = document.createRange();
                range.selectNodeContents(tempInput);
                const selection = window.getSelection();
                selection.removeAllRanges();
                selection.addRange(range);
                tempInput.setSelectionRange(0, 999999);
            } else {
                tempInput.select();
            }

            // Use execCommand as fallback (still works in iOS Safari)
            const success = document.execCommand("copy");
            document.body.removeChild(tempInput);
            resolve(success);
        } catch (error) {
            console.error("Fallback copy failed:", error);
            resolve(false);
        }
    });
};

/**
 * Copy text to clipboard - cross-browser compatible
 * Tries modern API first, falls back to execCommand for iOS Safari
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} - Success status
 */
const copyText = async (text) => {
    // For iOS devices, always use fallback method as it's more reliable
    if (isIOSDevice()) {
        return await copyTextFallback(text);
    }

    // Try modern Clipboard API first for non-iOS browsers
    if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch (error) {
            console.warn("Clipboard API failed, using fallback:", error);
            return await copyTextFallback(text);
        }
    }

    // Fallback for older browsers
    return await copyTextFallback(text);
};

/**
 * Copy image from base64 data URL to clipboard
 * Note: Image copy is NOT supported on iOS Safari - will return false
 * @param {string} imageDataUrl - Base64 image data URL
 * @returns {Promise<boolean>} - Success status
 */
export const copyImage = async (imageDataUrl) => {
    try {
        // iOS Safari does not support copying images to clipboard
        if (isIOSDevice()) {
            console.warn("Image copy not supported on iOS Safari");
            return false;
        }

        if (!navigator.clipboard || !window.ClipboardItem) {
            console.warn("Clipboard API not supported for images");
            return false;
        }

        // Convert base64 to blob
        const response = await fetch(imageDataUrl);
        const blob = await response.blob();

        // Create clipboard item with image
        const clipboardItem = new ClipboardItem({
            [blob.type]: blob,
        });

        await navigator.clipboard.write([clipboardItem]);
        return true;
    } catch (error) {
        console.error("Failed to copy image:", error);
        return false;
    }
};

/**
 * Copy message with image and/or text
 * Handles all combinations: image only, text only, or both
 * For iOS Safari: only text copy is supported, images will be skipped
 * @param {Object} options - Copy options
 * @param {string} options.imageDataUrl - Base64 image data URL (optional)
 * @param {string} options.htmlContent - HTML content (optional)
 * @param {string} options.plainText - Plain text fallback (optional)
 * @returns {Promise<boolean>} - Success status
 */
export const copyMessageContent = async ({ imageDataUrl, htmlContent, plainText }) => {
    try {
        const isIOS = isIOSDevice();

        // Case 1: Image only (no text)
        if (imageDataUrl && !htmlContent && !plainText) {
            if (isIOS) {
                // iOS doesn't support image copy, return false
                console.warn("Image-only copy not supported on iOS Safari");
                return false;
            }
            return await copyImage(imageDataUrl);
        }

        // Case 2: Text only (no image) - works on all browsers including iOS Safari
        if (!imageDataUrl && (htmlContent || plainText)) {
            return await copyText(plainText || htmlToPlainText(htmlContent));
        }

        // Case 3: Both image and text
        if (imageDataUrl && (htmlContent || plainText)) {
            // For iOS, only copy text (image copy not supported)
            if (isIOS) {
                return await copyText(plainText || htmlToPlainText(htmlContent));
            }

            // For non-iOS browsers, try to copy both using modern Clipboard API
            if (navigator.clipboard && window.ClipboardItem) {
                try {
                    // Create HTML with embedded image reference
                    const combinedHtml = `<img src="${imageDataUrl}" /><br>${htmlContent || plainText}`;
                    const htmlBlob = new Blob([combinedHtml], { type: "text/html" });
                    const textBlob = new Blob([plainText || htmlContent], { type: "text/plain" });

                    const clipboardItem = new ClipboardItem({
                        "text/html": htmlBlob,
                        "text/plain": textBlob,
                    });

                    await navigator.clipboard.write([clipboardItem]);
                    return true;
                } catch (error) {
                    console.warn("Failed to copy image+text together, falling back to text only", error);
                    // Fallback: copy text only
                    return await copyText(plainText || htmlToPlainText(htmlContent));
                }
            }

            // Fallback: copy text only
            return await copyText(plainText || htmlToPlainText(htmlContent));
        }

        return false;
    } catch (error) {
        console.error("Failed to copy message content:", error);
        return false;
    }
};

/**
 * Copy formatted HTML content to clipboard while preserving formatting
 * Works on all browsers including iOS Safari (text only on iOS)
 * @param {string} htmlContent - HTML content to copy
 * @param {string} plainText - Fallback plain text
 * @returns {Promise<boolean>} - Success status
 */
export const copyFormattedContent = async (htmlContent, plainText) => {
    try {
        const isIOS = isIOSDevice();

        // For iOS, use text-only fallback method
        if (isIOS) {
            return await copyText(plainText);
        }

        // Modern Clipboard API with HTML support (preferred for non-iOS)
        if (navigator.clipboard && window.ClipboardItem) {
            try {
                // Create blob with HTML and plain text
                const htmlBlob = new Blob([htmlContent], { type: "text/html" });
                const textBlob = new Blob([plainText], { type: "text/plain" });

                const clipboardItem = new ClipboardItem({
                    "text/html": htmlBlob,
                    "text/plain": textBlob,
                });

                await navigator.clipboard.write([clipboardItem]);
                return true;
            } catch (error) {
                console.warn("ClipboardItem failed, using text fallback:", error);
                return await copyText(plainText);
            }
        }

        // Fallback: Copy plain text only
        return await copyText(plainText);
    } catch (error) {
        console.error("Failed to copy content:", error);
        return await copyText(plainText);
    }
};

/**
 * Convert HTML to plain text while preserving structure
 * @param {string} html - HTML content
 * @returns {string} - Plain text with preserved structure
 */
export const htmlToPlainText = (html) => {
    if (!html || typeof html !== "string") return "";

    let text = html;

    // Convert headings to text with markers
    text = text.replace(/<h1>(.*?)<\/h1>/gi, "\n\n# $1\n\n");
    text = text.replace(/<h2>(.*?)<\/h2>/gi, "\n\n## $1\n\n");
    text = text.replace(/<h3>(.*?)<\/h3>/gi, "\n\n### $1\n\n");

    // Convert bold and italic
    text = text.replace(/<strong>(.*?)<\/strong>/gi, "**$1**");
    text = text.replace(/<em>(.*?)<\/em>/gi, "*$1*");
    text = text.replace(/<del>(.*?)<\/del>/gi, "~~$1~~");

    // Convert code blocks
    text = text.replace(/<pre[^>]*><code[^>]*>(.*?)<\/code><\/pre>/gis, "\n```\n$1\n```\n");

    // Convert inline code
    text = text.replace(/<code[^>]*>(.*?)<\/code>/gi, "`$1`");

    // Convert links
    text = text.replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, "[$2]($1)");

    // Convert lists
    text = text.replace(/<li>(.*?)<\/li>/gi, "- $1\n");
    text = text.replace(/<\/ul>|<ul>/gi, "\n");
    text = text.replace(/<\/ol>|<ol>/gi, "\n");

    // Convert blockquotes
    text = text.replace(/<blockquote>(.*?)<\/blockquote>/gi, "> $1\n");

    // Convert line breaks and paragraphs
    text = text.replace(/<br\s*\/?>/gi, "\n");
    text = text.replace(/<\/p>/gi, "\n\n");
    text = text.replace(/<p>/gi, "");

    // Convert tables
    text = text.replace(/<table[^>]*>(.*?)<\/table>/gis, (match, tableContent) => {
        let result = "\n";

        // Extract headers
        const headerMatch = tableContent.match(/<thead>(.*?)<\/thead>/is);
        if (headerMatch) {
            const headers = headerMatch[1].match(/<th[^>]*>(.*?)<\/th>/gi);
            if (headers) {
                const headerTexts = headers.map((h) => h.replace(/<[^>]*>/g, "").trim());
                result += "| " + headerTexts.join(" | ") + " |\n";
                result += "|" + headerTexts.map(() => "---").join("|") + "|\n";
            }
        }

        // Extract body rows
        const bodyMatch = tableContent.match(/<tbody>(.*?)<\/tbody>/is);
        if (bodyMatch) {
            const rows = bodyMatch[1].match(/<tr[^>]*>.*?<\/tr>/gi);
            if (rows) {
                rows.forEach((row) => {
                    const cells = row.match(/<td[^>]*>(.*?)<\/td>/gi);
                    if (cells) {
                        const cellTexts = cells.map((c) => c.replace(/<[^>]*>/g, "").trim());
                        result += "| " + cellTexts.join(" | ") + " |\n";
                    }
                });
            }
        }

        return result + "\n";
    });

    // Remove remaining HTML tags
    text = text.replace(/<[^>]*>/g, "");

    // Decode HTML entities
    text = text
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'");

    // Clean up excessive whitespace
    text = text.replace(/\n{3,}/g, "\n\n");
    text = text.trim();

    return text;
};

export default copyFormattedContent;
